import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Flag, Check, ChevronDown, ChevronRight, Settings, RefreshCw, List, ListTree } from 'lucide-react';
import api from './api';
import { parseQuery, matchesQuery, isEmptyQuery } from './taskQuery';
import { keyBetween } from './sortKey';
import OutlineView, { keyAfterSibling } from './taskOutline';

// Градации зелёного (бренд — #2AAE9C)
const PRIORITY_COLORS = {
  1: '#006B59', // P1
  2: '#2AAE9C', // P2 (бренд)
  3: '#7FCFC4', // P3
  4: '#adb5bd', // P4 серый
};

function isOverdue(dueDate) {
  if (!dueDate) return false;
  const d = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

function formatDue(dueDate) {
  if (!dueDate) return '';
  const d = new Date(dueDate);
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

// Бейджи источника для задач, приехавших с внешних площадок.
const SOURCE_BADGES = {
  bemulti: { label: 'бимульти', bg: '#e7f1ff', color: '#0d6efd' },
  delolet: { label: 'делолет', bg: '#e8f8f0', color: '#198754' },
};

function TaskRow({
  task, depth, onToggle, onDelete, onCyclePriority,
  expanded, onExpandToggle,
  menuOpen, onMenuToggle, rootCandidates, onSetParent,
  onUploadFile, onDeleteFile, uploading,
  draggable, onDragStart, onDragOver, onDrop, onDragEnd, dropTarget,
  overdueBadge, onForceDone,
  currentUserId, onOpenChat,
}) {
  const [hover, setHover] = useState(false);
  const fileRef = useRef(null);
  const color = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS[4];
  const overdue = !task.isDone && isOverdue(task.dueDate);
  const attachments = task.attachments || [];

  return (
    <div>
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        draggable={draggable}
        onDragStart={draggable ? (e) => onDragStart(e, task) : undefined}
        onDragOver={draggable ? (e) => onDragOver(e, task) : undefined}
        onDrop={draggable ? (e) => onDrop(e, task) : undefined}
        onDragEnd={draggable ? onDragEnd : undefined}
        onClick={() => onExpandToggle(task)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px', paddingLeft: 16 + depth * 20,
          borderBottom: '1px solid #f1f3f5',
          borderTop: dropTarget ? '2px solid #2AAE9C' : '2px solid transparent',
          background: hover ? '#f8f9fa' : 'white',
          transition: 'background 0.15s',
          cursor: draggable ? 'grab' : 'default',
        }}
      >
        {/* Round checkbox */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(task); }}
          title={task.isDone ? 'Вернуть в работу' : 'Выполнено'}
          style={{
            width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
            border: `2px solid ${task.isDone ? '#7B2FF7' : color}`,
            background: task.isDone ? '#7B2FF7' : 'transparent',
            cursor: 'pointer', padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white',
          }}
        >
          {task.isDone && <Check size={12} strokeWidth={3} />}
        </button>

        {/* Title + due date */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, color: task.isDone ? '#adb5bd' : '#333',
            textDecoration: task.isDone ? 'line-through' : 'none',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {task.title}
            {task.recurrence && (
              <span title={task.recurrence} style={{ marginLeft: 6, fontSize: 12, cursor: 'default' }}>🔁</span>
            )}
            {attachments.length > 0 && (
              <span style={{ marginLeft: 6, fontSize: 12, color: '#888' }}>📎{attachments.length}</span>
            )}
            {overdueBadge && (
              <span style={{
                marginLeft: 6, padding: '1px 7px', borderRadius: 8, fontSize: 11,
                background: '#fdecea', color: '#dc3545', verticalAlign: 'middle', fontWeight: 600,
              }}>
                просрочено
              </span>
            )}
            {SOURCE_BADGES[task.source] && (
              <span style={{
                marginLeft: 6, padding: '1px 7px', borderRadius: 8, fontSize: 11,
                background: SOURCE_BADGES[task.source].bg,
                color: SOURCE_BADGES[task.source].color,
                verticalAlign: 'middle',
              }}>
                {SOURCE_BADGES[task.source].label}
              </span>
            )}
          </div>
          {(task.dueDate || (task.creatorId && task.creatorId !== currentUserId) || (task.sourceChatId || task.originChatId)) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 1, minWidth: 0 }}>
              {task.dueDate && (
                <span style={{
                  fontSize: 12, flexShrink: 0,
                  color: overdue ? '#006B59' : '#888',
                  fontWeight: overdue ? 700 : 400,
                }}>
                  {formatDue(task.dueDate)}
                </span>
              )}
              {/* Бейдж «от {имя}» для назначенных задач */}
              {task.creatorId && task.creatorId !== currentUserId && (
                <span style={{ fontSize: 11, color: '#888', flexShrink: 0 }}>
                  от {task.creator?.displayName || task.creator?.username || 'коллеги'}
                </span>
              )}
              {/* Чип чата-источника: клик открывает переписку */}
              {(task.sourceChatId || task.originChatId) && (
                <button
                  onClick={(e) => { e.stopPropagation(); onOpenChat && onOpenChat((task.sourceChatId || task.originChatId)); }}
                  title="Открыть переписку"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                    padding: '0 7px', height: 17, borderRadius: 9, border: 'none',
                    background: '#eef0f2', color: '#667781', fontSize: 11, cursor: 'pointer',
                    maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  💬 {task.sourceChatTitle || 'чат'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Priority flag — click to cycle P1→P2→P3→P4 */}
        <button
          onClick={(e) => { e.stopPropagation(); onCyclePriority(task); }}
          title={`Приоритет P${task.priority} — нажмите, чтобы изменить`}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            width: 28, height: 28, borderRadius: 6, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color,
          }}
        >
          <Flag size={15} fill={task.priority < 4 ? color : 'none'} />
        </button>

        {/* ⋯ menu: подзадачи */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button
            onClick={(e) => { e.stopPropagation(); onMenuToggle(task); }}
            title="Ещё"
            style={{
              background: menuOpen ? '#e9ecef' : 'transparent', border: 'none', cursor: 'pointer',
              width: 24, height: 24, borderRadius: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#888', fontSize: 16, lineHeight: 1,
            }}
          >
            ⋯
          </button>
          {menuOpen && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute', right: 0, top: 26, zIndex: 20,
                background: 'white', border: '1px solid #e1e5e9', borderRadius: 10,
                boxShadow: '0 6px 20px rgba(0,0,0,0.12)', minWidth: 220,
                padding: 6, maxHeight: 260, overflowY: 'auto',
              }}
            >
              {task.recurrence && !task.isDone && (
                <button
                  onClick={() => onForceDone(task)}
                  title="Закрыть повторяющуюся задачу без переноса на следующий раз"
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, borderRadius: 6, color: '#dc3545' }}
                >
                  Завершить навсегда
                </button>
              )}
              {task.parentId && (
                <button
                  onClick={() => onSetParent(task, null)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, borderRadius: 6 }}
                >
                  Сделать корневой
                </button>
              )}
              <div style={{ padding: '7px 10px 3px', fontSize: 11, color: '#adb5bd', fontWeight: 600 }}>
                Сделать подзадачей…
              </div>
              {rootCandidates.filter(t => t.id !== task.id).length === 0 && (
                <div style={{ padding: '4px 10px 8px', fontSize: 12, color: '#adb5bd' }}>Нет других корневых задач</div>
              )}
              {rootCandidates.filter(t => t.id !== task.id).map(t => (
                <button
                  key={t.id}
                  onClick={() => onSetParent(task, t.id)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px',
                    border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, borderRadius: 6,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {t.title}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Delete on hover */}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(task); }}
          title="Удалить"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            width: 24, height: 24, borderRadius: 6, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#aaa', fontSize: 18, lineHeight: 1,
            visibility: hover ? 'visible' : 'hidden',
          }}
        >
          ×
        </button>
      </div>

      {/* Мини-панель вложений */}
      {expanded && (
        <div style={{
          padding: '8px 16px 10px', paddingLeft: 16 + depth * 20 + 30,
          background: '#fbfcfd', borderBottom: '1px solid #f1f3f5',
        }}>
          {attachments.map(a => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
              <button
                onClick={() => window.open(a.url, '_blank', 'noopener')}
                title={a.mimeType}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
                  fontSize: 13, color: '#2AAE9C', textAlign: 'left', flex: 1, minWidth: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                📎 {a.fileName}
              </button>
              <span style={{ fontSize: 11, color: '#adb5bd', flexShrink: 0 }}>{formatSize(a.size)}</span>
              <button
                onClick={() => onDeleteFile(task, a)}
                title="Удалить файл"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 15, lineHeight: 1, padding: 0 }}
              >
                ×
              </button>
            </div>
          ))}
          <input
            ref={fileRef}
            type="file"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              if (f) onUploadFile(task, f);
              e.target.value = '';
            }}
          />
          <button
            onClick={() => fileRef.current && fileRef.current.click()}
            disabled={uploading}
            style={{
              marginTop: 4, padding: '5px 12px', border: '1px dashed #2AAE9C', borderRadius: 8,
              background: 'white', color: '#2AAE9C', fontSize: 12, cursor: 'pointer',
              opacity: uploading ? 0.6 : 1,
            }}
          >
            {uploading ? 'Загрузка…' : '+ Прикрепить файл'}
          </button>
        </div>
      )}
    </div>
  );
}

// Форма подключения одной внешней площадки.
function IntegrationCard({ provider, title, hint, integration, onSaved, onRemoved }) {
  const [login, setLogin] = useState(integration?.login || '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const connect = async () => {
    if (!login.trim() || !password) { setMsg({ err: true, text: 'Введите логин и пароль' }); return; }
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post('/integrations', { provider, login: login.trim(), password });
      setMsg({ err: false, text: `Подключено, задач получено: ${data.imported}` });
      setPassword('');
      onSaved();
    } catch (e) {
      setMsg({ err: true, text: e.response?.data?.message || 'Ошибка подключения' });
    } finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post(`/integrations/${provider}/sync`);
      setMsg({ err: false, text: `Синхронизировано, открытых задач: ${data.imported}` });
      onSaved();
    } catch (e) {
      setMsg({ err: true, text: e.response?.data?.message || 'Ошибка синхронизации' });
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await api.delete(`/integrations/${provider}`); onRemoved(); } catch {}
    setBusy(false);
  };

  const inputStyle = { width: '100%', padding: '7px 12px', border: '1px solid #e1e5e9', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 6 };

  return (
    <div style={{ border: '1px solid #e1e5e9', borderRadius: 12, padding: 14, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{title}</div>
        {integration && (
          <span style={{ fontSize: 12, color: integration.lastError ? '#dc3545' : '#198754' }}>
            {integration.lastError ? 'ошибка' : 'подключено'}
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>{hint}</div>
      <input style={inputStyle} placeholder="Email" value={login} onChange={e => setLogin(e.target.value)} />
      <input style={inputStyle} placeholder={integration ? 'Пароль (для смены)' : 'Пароль'} type="password" value={password} onChange={e => setPassword(e.target.value)} />
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button onClick={connect} disabled={busy} style={{ flex: 1, padding: '7px 0', border: 'none', borderRadius: 8, background: '#7B2FF7', color: 'white', fontSize: 13, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
          {integration ? 'Обновить' : 'Подключить'}
        </button>
        {integration && (
          <>
            <button onClick={syncNow} disabled={busy} title="Синхронизировать сейчас" style={{ padding: '7px 10px', border: '1px solid #e1e5e9', borderRadius: 8, background: 'white', cursor: 'pointer' }}>
              <RefreshCw size={14} />
            </button>
            <button onClick={disconnect} disabled={busy} style={{ padding: '7px 10px', border: '1px solid #f1aeb5', borderRadius: 8, background: 'white', color: '#dc3545', fontSize: 13, cursor: 'pointer' }}>
              Отключить
            </button>
          </>
        )}
      </div>
      {msg && <div style={{ marginTop: 8, fontSize: 12, color: msg.err ? '#dc3545' : '#198754' }}>{msg.text}</div>}
      {integration?.lastError && !msg && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#dc3545' }}>{integration.lastError}</div>
      )}
      {integration?.lastSyncAt && (
        <div style={{ marginTop: 4, fontSize: 11, color: '#adb5bd' }}>
          Синк: {new Date(integration.lastSyncAt).toLocaleString('ru-RU')}
        </div>
      )}
    </div>
  );
}

export default function TasksPanel({ onClose, currentUserId, onOpenChat }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [showDone, setShowDone] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [menuTaskId, setMenuTaskId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const dragIdRef = useRef(null);
  const [tab, setTab] = useState('today'); // today | all | done
  const [search, setSearch] = useState('');
  const [savedFilters, setSavedFilters] = useState([]);
  const [undoTask, setUndoTask] = useState(null); // задача, которую только что закрыли
  const undoTimerRef = useRef(null);
  // Режим отображения: «Список» (плоские приоритеты + вкладки) | «Аутлайн» (Workflowy-дерево)
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('terkaTaskViewMode') || 'list'; } catch { return 'list'; }
  });
  const toggleViewMode = () => {
    setViewMode(prev => {
      const next = prev === 'list' ? 'outline' : 'list';
      try { localStorage.setItem('terkaTaskViewMode', next); } catch {}
      return next;
    });
  };

  useEffect(() => () => clearTimeout(undoTimerRef.current), []);

  const loadIntegrations = useCallback(async () => {
    try {
      const { data } = await api.get('/integrations');
      if (data.success) setIntegrations(data.integrations);
    } catch {}
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/tasks');
      if (data.success) setTasks(data.tasks);
    } catch {} finally { setLoading(false); }
  }, []);

  const loadFilters = useCallback(async () => {
    try {
      const { data } = await api.get('/tasks/filters');
      if (data.success) setSavedFilters(data.filters);
    } catch {}
  }, []);

  useEffect(() => { load(); loadIntegrations(); loadFilters(); }, [load, loadIntegrations, loadFilters]);

  const saveCurrentFilter = async () => {
    const query = search.trim();
    if (!query) return;
    const name = (window.prompt('Название фильтра:', query) || '').trim();
    if (!name) return;
    try {
      const { data } = await api.post('/tasks/filters', { name, query });
      if (data.filter) setSavedFilters(prev => [...prev, data.filter]);
    } catch (e) {
      alert(e.response?.data?.message || 'Не удалось сохранить фильтр');
    }
  };

  const deleteFilter = async (f) => {
    if (!window.confirm(`Удалить фильтр «${f.name}»?`)) return;
    setSavedFilters(prev => prev.filter(x => x.id !== f.id));
    try { await api.delete(`/tasks/filters/${f.id}`); } catch { loadFilters(); }
  };

  // Ключ ПЕРЕД первой задачей списка: клиент генерирует sortKey сам,
  // сервер сохраняет как есть — после ответа позиция не меняется (без «прыжков»).
  const sortKeyBeforeAll = useCallback((list) => {
    const keys = list.filter(t => !t.isDone && !t.parentId && t.sortKey).map(t => t.sortKey).sort();
    return keyBetween(null, keys[0] || null);
  }, []);

  const addTask = async () => {
    const title = newTitle.trim();
    if (!title) return;
    setNewTitle('');
    const sortKey = sortKeyBeforeAll(tasks);
    const tempId = `temp-${Date.now()}`;
    // Optimistic: временная задача сразу в начале с тем же sortKey, что уйдёт на сервер
    setTasks(prev => [{ id: tempId, title, sortKey, parentId: null, isDone: false, priority: 4, dueDate: null, attachments: [] }, ...prev]);
    try {
      // Сервер сам распознаёт русские даты/повторы в тексте.
      const { data } = await api.post('/tasks', { title, sortKey });
      if (data.task) {
        setTasks(prev => prev.map(t => t.id === tempId
          ? { ...data.task, sortKey: data.task.sortKey || sortKey }
          : t));
      } else {
        setTasks(prev => prev.filter(t => t.id !== tempId));
      }
    } catch {
      setTasks(prev => prev.filter(t => t.id !== tempId));
    }
  };

  const showUndoToast = (task) => {
    setUndoTask(task);
    clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => setUndoTask(null), 5000);
  };

  const toggleDone = async (task) => {
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, isDone: !t.isDone } : t));
    if (!task.isDone) showUndoToast(task);
    try {
      const { data } = await api.put(`/tasks/${task.id}`, { isDone: !task.isDone });
      // Повторяющаяся задача вернётся с isDone=false и новым dueDate — заменяем из ответа
      if (data && data.task) {
        setTasks(prev => prev.map(t => t.id === task.id ? data.task : t));
      }
    } catch { load(); }
  };

  const undoDone = async () => {
    const task = undoTask;
    if (!task) return;
    clearTimeout(undoTimerRef.current);
    setUndoTask(null);
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, isDone: false } : t));
    try {
      const { data } = await api.put(`/tasks/${task.id}`, { isDone: false });
      if (data && data.task) setTasks(prev => prev.map(t => t.id === task.id ? data.task : t));
    } catch { load(); }
  };

  // Повторяющаяся задача: закрыть навсегда, без переноса на следующий раз
  const forceDone = async (task) => {
    setMenuTaskId(null);
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, isDone: true } : t));
    try {
      const { data } = await api.put(`/tasks/${task.id}`, { isDone: true, forceDone: true });
      if (data && data.task) setTasks(prev => prev.map(t => t.id === task.id ? data.task : t));
    } catch { load(); }
  };

  const cyclePriority = async (task) => {
    const next = task.priority >= 4 ? 1 : task.priority + 1;
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, priority: next } : t));
    try {
      await api.put(`/tasks/${task.id}`, { priority: next });
    } catch { load(); }
  };

  const deleteTask = async (task) => {
    setTasks(prev => prev.filter(t => t.id !== task.id));
    try {
      await api.delete(`/tasks/${task.id}`);
    } catch { load(); }
  };

  const setParent = async (task, parentId) => {
    setMenuTaskId(null);
    // В конец детей нового родителя (или в конец корня), ключ считает клиент
    const sortKey = keyAfterSibling(tasks.filter(t => t.id !== task.id), parentId || null, null);
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, parentId, sortKey } : t));
    try {
      await api.post('/tasks/reorder', { items: [{ id: task.id, parentId: parentId || null, sortKey }] });
    } catch { load(); }
  };

  const uploadFile = async (task, file) => {
    setUploadingId(task.id);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post(`/tasks/${task.id}/attachments`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data && data.attachment) {
        setTasks(prev => prev.map(t => t.id === task.id
          ? { ...t, attachments: [...(t.attachments || []), data.attachment] }
          : t));
      }
    } catch {} finally { setUploadingId(null); }
  };

  const deleteFile = async (task, attachment) => {
    setTasks(prev => prev.map(t => t.id === task.id
      ? { ...t, attachments: (t.attachments || []).filter(a => a.id !== attachment.id) }
      : t));
    try {
      await api.delete(`/tasks/${task.id}/attachments/${attachment.id}`);
    } catch { load(); }
  };

  // --- Drag & drop перестановка активных задач ---
  const onDragStart = (e, task) => {
    dragIdRef.current = task.id;
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(task.id)); } catch {}
  };
  const onDragOver = (e, task) => {
    if (dragIdRef.current == null || dragIdRef.current === task.id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetId(task.id);
  };
  const onDrop = (e, target) => {
    e.preventDefault();
    const dragId = dragIdRef.current;
    setDropTargetId(null);
    dragIdRef.current = null;
    if (dragId == null || dragId === target.id) return;
    setTasks(prev => {
      const list = [...prev];
      const from = list.findIndex(t => t.id === dragId);
      const to = list.findIndex(t => t.id === target.id);
      if (from < 0 || to < 0) return prev;
      const [moved] = list.splice(from, 1);
      list.splice(list.findIndex(t => t.id === target.id), 0, moved);
      // Новый sortKey — между соседними корневыми в получившемся порядке
      const roots = list.filter(t => !t.isDone && !t.parentId);
      const mi = roots.findIndex(t => t.id === moved.id);
      const prevKey = mi > 0 ? roots[mi - 1].sortKey : null;
      const nextKey = mi >= 0 && mi < roots.length - 1 ? roots[mi + 1].sortKey : null;
      const sortKey = keyBetween(prevKey, nextKey);
      api.post('/tasks/reorder', { items: [{ id: moved.id, parentId: moved.parentId || null, sortKey }] })
        .catch(() => load());
      return list.map(t => t.id === moved.id ? { ...t, sortKey } : t);
    });
  };
  const onDragEnd = () => { dragIdRef.current = null; setDropTargetId(null); };

  // Доверяем порядку сервера (isDone, sortOrder, priority, createdAt) — без клиентской сортировки
  const active = tasks.filter(t => !t.isDone);
  const done = tasks.filter(t => t.isDone);
  const rootCandidates = tasks.filter(t => !t.parentId && !t.isDone);

  // «Сегодня»: активные с dueDate до конца сегодняшнего дня
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  const todayTasks = active.filter(t => t.dueDate && new Date(t.dueDate) <= endOfToday);
  const overdueTasks = todayTasks.filter(t => isOverdue(t.dueDate));
  const dueTodayTasks = todayTasks.filter(t => !isOverdue(t.dueDate));

  // Перенести все просроченные на сегодня 09:00
  const postponeOverdueToToday = async () => {
    const target = new Date();
    target.setHours(9, 0, 0, 0);
    const iso = target.toISOString();
    const ids = overdueTasks.map(t => t.id);
    setTasks(prev => prev.map(t => ids.includes(t.id) ? { ...t, dueDate: iso } : t));
    try {
      await Promise.all(ids.map(id => api.put(`/tasks/${id}`, { dueDate: iso })));
    } catch { load(); }
  };

  // Мини-язык фильтров: непустой запрос — плоский список поверх табов
  const query = parseQuery(search);
  const searchActive = !isEmptyQuery(query);
  const searchResults = searchActive ? tasks.filter(t => matchesQuery(query, t)) : [];

  // Дерево: рекурсивный рендер по parentId
  const knownIds = new Set(tasks.map(t => t.id));
  const renderTree = (list, allList, draggableRoots, withOverdueBadge = false) => {
    const roots = list.filter(t => !t.parentId || !knownIds.has(t.parentId));
    const rows = [];
    const walk = (task, depth) => {
      rows.push(
        <TaskRow
          key={task.id}
          task={task}
          depth={depth}
          onToggle={toggleDone}
          onDelete={deleteTask}
          onCyclePriority={cyclePriority}
          expanded={expandedId === task.id}
          onExpandToggle={(t) => { setExpandedId(prev => prev === t.id ? null : t.id); setMenuTaskId(null); }}
          menuOpen={menuTaskId === task.id}
          onMenuToggle={(t) => setMenuTaskId(prev => prev === t.id ? null : t.id)}
          rootCandidates={rootCandidates}
          onSetParent={setParent}
          onUploadFile={uploadFile}
          onDeleteFile={deleteFile}
          uploading={uploadingId === task.id}
          draggable={draggableRoots && depth === 0}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onDragEnd={onDragEnd}
          dropTarget={dropTargetId === task.id}
          overdueBadge={withOverdueBadge && !task.isDone && isOverdue(task.dueDate)}
          onForceDone={forceDone}
          currentUserId={currentUserId}
          onOpenChat={onOpenChat}
        />
      );
      allList.filter(c => c.parentId === task.id).forEach(c => walk(c, depth + 1));
    };
    roots.forEach(t => walk(t, 0));
    return rows;
  };

  // Плоский список результатов фильтра (без дерева — иначе совпавшие подзадачи невидимы)
  const renderFlat = (list) => list.map(task => (
    <TaskRow
      key={task.id}
      task={task}
      depth={0}
      onToggle={toggleDone}
      onDelete={deleteTask}
      onCyclePriority={cyclePriority}
      expanded={expandedId === task.id}
      onExpandToggle={(t) => { setExpandedId(prev => prev === t.id ? null : t.id); setMenuTaskId(null); }}
      menuOpen={menuTaskId === task.id}
      onMenuToggle={(t) => setMenuTaskId(prev => prev === t.id ? null : t.id)}
      rootCandidates={rootCandidates}
      onSetParent={setParent}
      onUploadFile={uploadFile}
      onDeleteFile={deleteFile}
      uploading={uploadingId === task.id}
      draggable={false}
      overdueBadge={!task.isDone && isOverdue(task.dueDate)}
      onForceDone={forceDone}
      currentUserId={currentUserId}
      onOpenChat={onOpenChat}
    />
  ));

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0,
      width: 400, maxWidth: '90vw',
      background: 'white',
      boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
      zIndex: 90,
      display: 'flex', flexDirection: 'column',
      animation: 'slideIn 0.25s ease',
    }}>
      <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7B2FF7 0%, #E040A0 100%)',
        color: 'white',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white' }}>
          <X size={18} />
        </button>
        <div style={{ flex: 1, fontSize: 18, fontWeight: 600 }}>Задачи</div>
        <button
          onClick={toggleViewMode}
          title={viewMode === 'list' ? 'Переключить на «Аутлайн»' : 'Переключить на «Список»'}
          style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white' }}
        >
          {viewMode === 'list' ? <ListTree size={17} /> : <List size={17} />}
        </button>
        <button
          onClick={() => setShowSettings(v => !v)}
          title="Подключение площадок (бимульти, делолет)"
          style={{ background: showSettings ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white' }}
        >
          <Settings size={17} />
        </button>
      </div>

      {/* Integrations settings */}
      {showSettings && (
        <div style={{ padding: 16, borderBottom: '1px solid #e1e5e9', overflowY: 'auto', maxHeight: '55%' }}>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
            Подключи свои площадки — их открытые задачи появятся в этом списке.
            Отметка «выполнено» здесь закрывает задачу и на площадке.
          </div>
          <IntegrationCard
            provider="bemulti"
            title="Бимульти (4mama.ru)"
            hint="Задачи из /admin/todo Университета мам. Вход — email и пароль админки."
            integration={integrations.find(i => i.provider === 'bemulti')}
            onSaved={() => { loadIntegrations(); load(); }}
            onRemoved={() => { loadIntegrations(); load(); }}
          />
          <IntegrationCard
            provider="delolet"
            title="Делолет (delolet.ru)"
            hint="CRM-задачи (звонки, встречи, дедлайны). Вход — email и пароль."
            integration={integrations.find(i => i.provider === 'delolet')}
            onSaved={() => { loadIntegrations(); load(); }}
            onRemoved={() => { loadIntegrations(); load(); }}
          />
        </div>
      )}

      {/* Quick add */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #e1e5e9' }}>
        <input
          value={newTitle}
          onChange={e => setNewTitle(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addTask(); }}
          placeholder="Новая задача… (во вторник в 12, каждый понедельник в 17:30)"
          autoFocus
          style={{ width: '100%', padding: '8px 14px', border: '1px solid #e1e5e9', borderRadius: 20, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
          onFocus={e => e.target.style.borderColor = '#7B2FF7'}
          onBlur={e => e.target.style.borderColor = '#e1e5e9'}
        />
      </div>

      {/* Строка фильтра + сохранённые фильтры */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid #e1e5e9' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Escape') setSearch(''); }}
          placeholder="p1 today отчёт — фильтры и текст вместе"
          style={{ width: '100%', padding: '7px 12px', border: '1px solid #e1e5e9', borderRadius: 16, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
          onFocus={e => e.target.style.borderColor = '#2AAE9C'}
          onBlur={e => e.target.style.borderColor = '#e1e5e9'}
        />
        {searchActive && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <span style={{ fontSize: 12, color: '#888', flex: 1 }}>
              {searchResults.length} задач
            </span>
            <button
              onClick={saveCurrentFilter}
              style={{ padding: '4px 10px', border: '1px solid #2AAE9C', borderRadius: 8, background: 'white', color: '#2AAE9C', fontSize: 12, cursor: 'pointer' }}
            >
              Сохранить фильтр
            </button>
            <button
              onClick={() => setSearch('')}
              title="Очистить фильтр"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#888', fontSize: 16, lineHeight: 1, padding: '0 2px' }}
            >
              ×
            </button>
          </div>
        )}
        {savedFilters.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {savedFilters.map(f => (
              <span
                key={f.id}
                onContextMenu={(e) => { e.preventDefault(); deleteFilter(f); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 8px', borderRadius: 12, fontSize: 12,
                  background: search.trim() === f.query ? '#e0f5f1' : '#f1f3f5',
                  color: search.trim() === f.query ? '#006B59' : '#495057',
                }}
              >
                <button
                  onClick={() => setSearch(f.query)}
                  title={f.query}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, color: 'inherit' }}
                >
                  {f.name}
                </button>
                <button
                  onClick={() => deleteFilter(f)}
                  title="Удалить фильтр"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, lineHeight: 1, color: '#adb5bd' }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      {!searchActive && viewMode === 'list' && (
      <div style={{ display: 'flex', borderBottom: '1px solid #e1e5e9' }}>
        {[
          ['today', 'Сегодня'],
          ['all', 'Все'],
          ['done', 'Выполненные'],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              flex: 1, padding: '10px 0', border: 'none', background: 'transparent',
              cursor: 'pointer', fontSize: 13, fontWeight: 600,
              color: tab === key ? '#7B2FF7' : '#888',
              borderBottom: tab === key ? '2px solid #7B2FF7' : '2px solid transparent',
            }}
          >
            {label}
          </button>
        ))}
      </div>
      )}

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Загрузка...</div>
        ) : searchActive ? (
          searchResults.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🔍</div>
              <div style={{ fontSize: 13, marginBottom: 12 }}>Ничего не найдено</div>
              {query.text && (
                <button
                  onClick={async () => {
                    const title = query.text;
                    setSearch('');
                    try {
                      const { data } = await api.post('/tasks', { title });
                      // В начало списка — единообразно с addTask.
                      if (data.task) setTasks(prev => [data.task, ...prev]);
                    } catch {}
                  }}
                  style={{ padding: '6px 14px', border: '1px solid #7B2FF7', borderRadius: 8, background: 'white', color: '#7B2FF7', fontSize: 13, cursor: 'pointer' }}
                >
                  Создать задачу «{query.text}»
                </button>
              )}
            </div>
          ) : renderFlat(searchResults)
        ) : viewMode === 'outline' ? (
          <OutlineView
            tasks={tasks}
            setTasks={setTasks}
            onToggleDone={toggleDone}
            onCyclePriority={cyclePriority}
            reload={load}
          />
        ) : tasks.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>Задач пока нет</div>
            <div style={{ fontSize: 13 }}>Добавьте первую задачу выше</div>
          </div>
        ) : tab === 'today' ? (
          todayTasks.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🎉</div>
              <div style={{ fontSize: 13 }}>На сегодня всё сделано</div>
            </div>
          ) : (
            <>
              {overdueTasks.length > 0 && (
                <>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px',
                    background: '#fff5f5', borderBottom: '1px solid #f1f3f5',
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#dc3545', flex: 1 }}>
                      Просрочено ({overdueTasks.length})
                    </span>
                    <button
                      onClick={postponeOverdueToToday}
                      style={{
                        padding: '4px 10px', border: '1px solid #dc3545', borderRadius: 8,
                        background: 'white', color: '#dc3545', fontSize: 12, cursor: 'pointer',
                      }}
                    >
                      Перенести всё на сегодня
                    </button>
                  </div>
                  {renderTree(overdueTasks, overdueTasks, false, true)}
                </>
              )}
              {renderTree(dueTodayTasks, dueTodayTasks, false)}
            </>
          )
        ) : tab === 'done' ? (
          done.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#888', fontSize: 13 }}>
              Выполненных задач пока нет
            </div>
          ) : renderTree(done, done, false)
        ) : (
          <>
            {renderTree(active, active, true)}

            {done.length > 0 && (
              <>
                <button
                  onClick={() => setShowDone(v => !v)}
                  style={{
                    width: '100%', padding: '10px 16px', border: 'none',
                    background: '#f8f9fa', borderTop: '1px solid #e1e5e9', borderBottom: '1px solid #e1e5e9',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                    fontSize: 13, fontWeight: 600, color: '#6c757d', textAlign: 'left',
                  }}
                >
                  {showDone ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  Выполненные ({done.length})
                </button>
                {showDone && renderTree(done, done, false)}
              </>
            )}
          </>
        )}
      </div>

      {/* Undo toast */}
      {undoTask && (
        <div style={{
          position: 'absolute', left: 16, right: 16, bottom: 16, zIndex: 30,
          background: '#333', color: 'white', borderRadius: 10,
          padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)', fontSize: 13,
        }}>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Выполнено · {undoTask.title}
          </span>
          <button
            onClick={undoDone}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#B794F6', fontSize: 13, fontWeight: 700, flexShrink: 0, padding: 0,
            }}
          >
            Отменить
          </button>
        </div>
      )}
    </div>
  );
}
